const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JSON_HEADERS = { "Content-Type": "application/json" };

// Serialize backend requests: only one in-flight at a time. The preview proxy
// stalls when multiple larger responses are requested concurrently.
let chain = Promise.resolve();
function enqueue(run) {
  const next = chain.then(run, run);
  chain = next.then(() => undefined, () => undefined);
  return next;
}

async function asJson(res) {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const t = await res.text();
  return JSON.parse(t);
}
function get(path) {
  return enqueue(() => fetch(`${BASE}${path}`).then(asJson));
}
function post(path, body) {
  return enqueue(() => fetch(`${BASE}${path}`, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) }).then(asJson));
}

export const api = {
  meta: () => get("/meta"),
  bundle: (city) => get(`/fine-bundle?city=${encodeURIComponent(city)}`),
  coarse: (city, params) => post("/coarse", { city, params }),
  grid: (city, params, validate_grid, max_iterations) => post("/grid", { city, params, validate_grid, max_iterations }),
  fineCounts: (city, params, weights) => post("/fine-counts", { city, params, weights }),
  calibCities: () => get("/calib-cities"),
  calibrate: (city) => post("/calibrate", { city }),
  download: (body) =>
    enqueue(() => fetch(`${BASE}/download`, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    })),
};

export function decodeU8(b64) {
  const s = atob(b64);
  const a = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i);
  return a;
}
export function decodeU16(b64) {
  const u8 = decodeU8(b64);
  return new Uint16Array(u8.buffer, 0, Math.floor(u8.byteLength / 2));
}
export function decodeI32(b64) {
  const u8 = decodeU8(b64);
  return new Int32Array(u8.buffer, 0, Math.floor(u8.byteLength / 4));
}

function hex2rgb(h) {
  h = h.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
export function makeColormap(stops, vmin, vmax) {
  const rgb = stops.map(hex2rgb);
  const span = vmax - vmin || 1;
  return (v) => {
    let t = (v - vmin) / span;
    t = Math.max(0, Math.min(1, t));
    const x = t * (rgb.length - 1);
    const i = Math.floor(x);
    const f = x - i;
    const a = rgb[i];
    const b = rgb[Math.min(i + 1, rgb.length - 1)];
    const c = a.map((av, k) => Math.round(av + (b[k] - av) * f));
    return `rgb(${c[0]},${c[1]},${c[2]})`;
  };
}

export const RAMPS = {
  pop: ["#fff5eb", "#fdd0a2", "#fd8d3c", "#d94801", "#7f2704"],
  indicator: ["#ffffd9", "#c7e9b4", "#41b6c4", "#225ea8", "#081d58"],
  priority: ["#ffffd9", "#c7e9b4", "#41b6c4", "#225ea8", "#081d58"],
  green: ["#f7fcf5", "#c7e9c0", "#74c476", "#238b45", "#00441b"],
  load: ["#deebf7", "#9ecae1", "#4292c6", "#08519c"],
  excess: ["#fee08b", "#fc8d59", "#d73027"],
};

// priority score per slot (0..1, normalized by max over masked slots)
export function priorityScores(vals, mask, leaves, weights) {
  const nl = leaves.length;
  const n = mask.length;
  const w = leaves.map((l) => weights[l] || 0);
  const score = new Float32Array(n);
  let mx = 0;
  for (let s = 0; s < n; s++) {
    if (!mask[s]) continue;
    let acc = 0;
    const base = s * nl;
    for (let k = 0; k < nl; k++) acc += (vals[base + k] / 255) * w[k];
    score[s] = acc;
    if (acc > mx) mx = acc;
  }
  if (mx > 0) for (let s = 0; s < n; s++) score[s] /= mx;
  return score;
}
